import { rememberProject } from '../../utils/projectPreference';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Dashboard from './Dashboard';
import backendApi from '../../services/backendApi';

/**
 * Composant wrapper pour le Dashboard qui gère le projet sélectionné
 */
const DashboardWithProject = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // Récupérer le projet sélectionné depuis localStorage
        const savedProjectId = localStorage.getItem('selected_project_id');

        if (!savedProjectId) {
          // Aucun projet sélectionné, rediriger vers la sélection
          navigate('/');
          return;
        }

        // Petit délai pour s'assurer que la session est établie
        await new Promise(resolve => setTimeout(resolve, 200));

        // Charger la liste des projets
        const response = await backendApi.getProjects();
        setProjects(response.projects || []);

        // Vérifier que le projet existe toujours
        const projectExists = response.projects?.some(
          p => p.id === parseInt(savedProjectId)
        );

        if (projectExists) {
          rememberProject(response.projects.find(p => p.id === parseInt(savedProjectId)));
          setSelectedProjectId(parseInt(savedProjectId));
        } else {
          // Le projet n'existe plus, rediriger vers la sélection
          localStorage.removeItem('selected_project_id');
          navigate('/');
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
        // En cas d'erreur, rediriger vers la sélection de projet
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [navigate]);

  /**
   * Gérer le changement de projet
   */
  const handleProjectChange = (newProjectId) => {
    setSelectedProjectId(newProjectId);
    rememberProject(projects.find(p => p.id === newProjectId) || { id: newProjectId });
  };

  /**
   * Gérer la déconnexion
   */
  const handleLogout = async () => {
    await logout();
    localStorage.removeItem('selected_project_id');
    navigate('/login');
  };

  // Afficher un loader pendant le chargement initial
  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-teal-600"></div>
      </div>
    );
  }

  // Ne rien afficher si aucun projet n'est sélectionné
  if (!selectedProjectId) {
    return null;
  }

  return (
    <Dashboard
      selectedProjectId={selectedProjectId}
      projects={projects}
      onProjectChange={handleProjectChange}
      onLogout={handleLogout}
    />
  );
};

export default DashboardWithProject;
